import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import _ from "lodash";

import { connect } from "react-redux";
import PActions from "../../Stores/redux/Persisted/Actions";
import UnpActions from "../../Stores/redux/Unpersisted/Actions";

import MainLayout from "../Common/MainLayout";
import DataTable from "../../Components/Table/DataTable";
import api from "../../Services/Api/api";
import AutoCompleteDropdownStatic from "../../Components/Input/AutoCompleteDropdownStatic";
import VisitReports from "../Common/VisitReports";
import GeoTaggedImage from "../../Components/Media/GeoTaggedImage";

const SchemeDetailsScreen = ({
  user,
  setScreenState,
  locationData,
  schemeTypes,
  schemeCategories,
}) => {
  const { schemeId } = useParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [scheme, setScheme] = useState({});
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({});
  const updateFormData = (obj) => setFormData((prev) => ({ ...prev, ...obj }));

  // Options for dropdowns
  const [subdivisionOptions, setSubdivisionOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [lacOptions, setLacOptions] = useState([]);
  const [circleOptions, setCircleOptions] = useState([]);
  const [deptBlockOptions, setDeptBlockOptions] = useState(null);

  useEffect(() => {
    if (!locationData) {
      loadLocationData();
    }

    fetchSchemeDetails();
  }, []);

  useEffect(() => {
    if (locationData) {
      setSubdivisionOptions([
        {
          items: locationData?.subdivisions?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setBlockOptions([
        {
          items: locationData?.blocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setDeptBlockOptions([
        {
          items: locationData?.deptBlocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setCircleOptions([
        {
          items: locationData?.circles?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setLacOptions([
        {
          items: locationData?.lacs?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
    }
  }, [locationData]);

  const loadLocationData = async () => {
    try {
      const locationData = await api.get("v1/appdata/location-data", {
        district: user.district._id,
      });
      setScreenState(
        { locationData: { ...locationData, uid: Date.now() } },
        false,
        "APP"
      );
    } catch (e) {
      toast.error("Error loading data: " + e.message);
    }
  };

  const fetchSchemeDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch updated scheme details
      const { schemes } = await api.get("v1/district-admin/scheme", {
        filter: JSON.stringify({
          where: { _id: schemeId, district: user.district?._id },
          populate: [
            { path: "district subdivision block circle lac" },
            {
              path: "office",
              populate: [{ path: "type", populate: [{ path: "category" }] }],
            },
          ],
          limit: 1,
        }),
      });
      console.log({ schemes });

      if (schemes?.length === 0) throw new Error("Scheme not found");

      const scheme = schemes[0];
      setScheme(scheme);

      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  // Edit, Cancel & Save Handlers
  const handleEdit = () => {
    if (!scheme._id) return;
    setFormData({
      ...scheme,
      office: scheme.office?._id,
      type: scheme.type?._id,
      category: scheme.type?.category?._id,
      deptBlock: scheme.deptBlock?._id,
      block: scheme.block?._id,
      subdivision: scheme.subdivision?._id,
      circle: scheme.circle?._id,
      lac: scheme.lac?._id,
    });
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSubmit = async () => {
    try {
      if (saving) return;
      setSaving(true);
      await api.put(`v1/district-admin/scheme/${scheme._id}`, {
        ..._.pick(formData, [
          "name",
          "lastDate",
          "amount",
          "office",
          "details",

          "block",
          "subdivision",
          "circle",
          "lac",
          "coord",
        ]),
      });
      fetchSchemeDetails();

      setIsEditing(false);
    } catch (error) {
      toast.error(error.message);
      console.error("Error updating scheme details:", error);
    }
    setSaving(false);
  };

  return (
    <MainLayout screenName="schemes">
      <div className="details-container">
        {/* Header Section */}
        <div className="details-header">
          <h2 className="details-title">Scheme Details</h2>
        </div>

        <div className="details-section">
          <div className="details-header">
            <h3>General Information</h3>
            {!isEditing ? (
              scheme?._id && (
                <button className="edit-btn" onClick={handleEdit}>
                  Edit
                </button>
              )
            ) : (
              <div>
                {!saving ? (
                  <button className="cancel-btn" onClick={handleCancel}>
                    Cancel
                  </button>
                ) : null}
                <button className="save-btn" onClick={handleSubmit}>
                  {saving ? "Saving.." : "Save"}
                </button>
              </div>
            )}
          </div>

          <div className={`details-content ${isEditing ? "editing" : ""}`}>
            {/* First Column */}
            <div className="details-column">
              {/* Scheme Name */}
              <div className="detail-row">
                <span className="label">Scheme Name</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name || ""}
                    onChange={(e) => updateFormData({ name: e.target.value })}
                  />
                ) : (
                  <span>{scheme.name || "-"}</span>
                )}
              </div>

              {/* Scheme Category */}
              <div className="detail-row">
                <span className="label">Last Date</span>
                {isEditing ? (
                  <input
                    type="date"
                    value={formData.lastDate || ""}
                    onChange={(e) =>
                      updateFormData({ lastDate: e.target.value })
                    }
                  />
                ) : (
                  <span>{scheme.lastDate || "-"}</span>
                )}
              </div>

              {/* Scheme Category */}
              <div className="detail-row">
                <span className="label">Amount</span>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.amount || ""}
                    onChange={(e) => updateFormData({ amount: e.target.value })}
                  />
                ) : (
                  <span>{scheme.amount || "-"}</span>
                )}
              </div>

              {/* HOD Phone */}
              <div className="detail-row">
                <span className="label">Office</span>
                {isEditing ? (
                  <span>{scheme.office?.name}</span>
                ) : scheme.hodPhone ? (
                  <span>{scheme.office?.name}</span>
                ) : (
                  <span>-</span>
                )}
              </div>

              <div className="detail-row">
                <span className="label">Details</span>

                {isEditing ? (
                  <textarea
                    value={formData.details || ""}
                    onChange={(e) =>
                      updateFormData({ details: e.target.value })
                    }
                  />
                ) : (
                  <span>{scheme.details || "-"}</span>
                )}
              </div>
            </div>

            {/* Second Column */}
            <div className="details-column">
              {/* Block */}
              <div className="detail-row">
                <span className="label">Block</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={blockOptions}
                    value={formData.block}
                    onSelect={(value) => updateFormData({ block: value })}
                  />
                ) : (
                  <span>{scheme.block?.name || "-"}</span>
                )}
              </div>

              {/* Subdivision */}
              <div className="detail-row">
                <span className="label">Subdivision</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={subdivisionOptions}
                    value={formData.subdivision}
                    onSelect={(value) => updateFormData({ subdivision: value })}
                  />
                ) : (
                  <span>{scheme.subdivision?.name || "-"}</span>
                )}
              </div>

              {/* Circle */}
              <div className="detail-row">
                <span className="label">Circle</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={circleOptions}
                    value={formData.circle}
                    onSelect={(value) => updateFormData({ circle: value })}
                  />
                ) : (
                  <span>{scheme.circle?.name || "-"}</span>
                )}
              </div>

              {/* LAC */}
              <div className="detail-row">
                <span className="label">LAC</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={lacOptions}
                    value={formData.lac}
                    onSelect={(value) => updateFormData({ lac: value })}
                  />
                ) : (
                  <span>{scheme.lac?.name || "-"}</span>
                )}
              </div>

              {/* GPS Coordinates */}
              <div className="detail-row">
                <span className="label">GPS Coordinates</span>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      placeholder="Latitude"
                      value={formData.coord?.lat || ""}
                      onChange={(e) =>
                        updateFormData({
                          coord: {
                            ...(formData.coord || {}),
                            lat: e.target.value,
                          },
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Longitude"
                      value={formData.coord?.lng || ""}
                      onChange={(e) =>
                        updateFormData({
                          coord: {
                            ...(formData.coord || {}),
                            lng: e.target.value,
                          },
                        })
                      }
                    />
                  </>
                ) : scheme.coord?.lat && scheme.coord?.lng ? (
                  <a
                    href={`https://www.google.com/maps?q=${scheme.coord.lat},${scheme.coord.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="map-link"
                  >
                    {scheme.coord.lat}, {scheme.coord.lng}
                  </a>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {scheme?.medias?.length > 0 && (
          <div className="details-section">
            <h3>Photos</h3>
            <div className="details-photo-gallery">
              {scheme?.medias?.map((file, i) => (
                <GeoTaggedImage
                  key={i}
                  src={api.getFileLink(file)}
                  alt="Scheme"
                  // className="details-photo"
                />
              ))}
            </div>
          </div>
        )}

        {scheme?._id ? (
          <div className="details-section">
            <h3>Visit Reports</h3>
            <VisitReports where={{ scheme: scheme._id }} />
          </div>
        ) : null}
      </div>
    </MainLayout>
  );
};

const SCREEN_NAME = "SCHEMES_SCREEN";

const mapStateToProps = (state) => ({
  user: state.pState.AUTH.user,
  locationData: state.vState.APP.locationData,
  schemeTypes: state.vState.APP.schemeTypes,
  schemeCategories: state.vState.APP.schemeCategories,
});

const mapDispatchToProps = (dispatch) => ({
  setScreenState: (obj, persist = false, screenName = SCREEN_NAME) =>
    persist
      ? dispatch(PActions.setPScreenState(screenName, obj))
      : dispatch(UnpActions.setVScreenState(screenName, obj)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SchemeDetailsScreen);
